import React, { useMemo } from "react";

/**
 * Iter-63.1 — Fleet-wide delivery-health KPI cards.
 *
 * Prefers the server-computed `fleet` summary (accurate across the whole
 * destination fleet, not just the current page). Falls back to a
 * page-scoped aggregate when the backend hasn't emitted `fleet_summary`
 * yet (older clients / staging) so we never render an empty header.
 */
export default function HealthSummaryCards({ rows, fleet }) {
  const summary = useMemo(() => {
    if (fleet) {
      return {
        t24: fleet.total || 0,
        s24: fleet.success || 0,
        f24: fleet.failure || 0,
        rate: fleet.success_rate === null || fleet.success_rate === undefined
          ? null
          : Number(fleet.success_rate),
        pending: fleet.pending_retries || 0,
        scope: "fleet",
      };
    }
    if (!rows) return null;
    const t24 = rows.reduce((a, r) => a + r.stats_24h.total, 0);
    const s24 = rows.reduce((a, r) => a + r.stats_24h.success, 0);
    const f24 = rows.reduce((a, r) => a + r.stats_24h.failure, 0);
    const qp = rows.reduce((a, r) => a + r.pending_retries, 0);
    const settled = s24 + f24;
    return {
      t24, s24, f24,
      rate: settled ? Math.round((s24 / settled) * 1000) / 10 : null,
      pending: qp,
      scope: "page",
    };
  }, [rows, fleet]);

  if (!summary) return null;
  const scopeLabel = summary.scope === "fleet" ? "24h · fleet-wide" : "24h · this page";

  return (
    <div className="grid grid-cols-4 gap-3 mb-4">
      <div className="brut-card p-4" data-testid="health-summary-total">
        <div className="label-mono">{scopeLabel} attempts</div>
        <div className="font-chivo font-black text-3xl">{summary.t24}</div>
      </div>
      <div className="brut-card p-4" data-testid="health-summary-rate">
        <div className="label-mono">{scopeLabel} success rate</div>
        <div className="font-chivo font-black text-3xl">
          {summary.rate === null ? "—" : `${summary.rate}%`}
        </div>
      </div>
      <div className="brut-card p-4" data-testid="health-summary-failed">
        <div className="label-mono">{scopeLabel} failed</div>
        <div className="font-chivo font-black text-3xl text-red-700">{summary.f24}</div>
      </div>
      <div className="brut-card p-4" data-testid="health-summary-pending">
        <div className="label-mono">Pending retries {summary.scope === "fleet" ? "· fleet" : "· page"}</div>
        <div className="font-chivo font-black text-3xl text-yellow-700">{summary.pending}</div>
      </div>
    </div>
  );
}
